import { useQueries, useQuery } from "@tanstack/react-query";

import { backofficeClient } from "@/lib/api/backoffice-client";

// Harmony ticket custom field definitions, for mapping them to provider
// fields. Served by service-desk's field configs: one tenant-wide config and
// one per desk.

export interface CustomFieldDefinition {
  /** The config field's UUID - stable across renames. */
  id: string;
  desk_id: string | null;
  name: string;
  field_type: string;
  is_internal: boolean;
}

/**
 * A field from the definitions table that predates the configs. Mappings
 * saved back then are keyed by its integer id, so it is still read to name
 * them.
 */
export interface LegacyCustomFieldDefinition {
  id: number;
  desk_id: string | null;
  name: string;
  field_type: string;
}

interface ConfigField {
  id: string;
  label: string;
  type: string;
  is_internal?: boolean | null;
}

interface CustomFieldConfigResponse {
  desk_id: string | null;
  custom_field_config: ConfigField[];
}

interface LegacyDefinitionsListResponse {
  items: LegacyCustomFieldDefinition[];
}

const CONFIG_URL = "/proxy/service-desk/api/v1/config/custom-fields/";
const LEGACY_BASE = "/proxy/service-desk/api/v1/desks";

async function fetchConfig(
  tenantId: string | undefined,
  deskId: string | null,
): Promise<CustomFieldDefinition[]> {
  const { data } = await backofficeClient.get<CustomFieldConfigResponse | null>(
    CONFIG_URL,
    { params: { tenant_id: tenantId, ...(deskId ? { desk_id: deskId } : {}) } },
  );
  return (data?.custom_field_config ?? []).map((field) => ({
    id: field.id,
    desk_id: deskId,
    name: field.label,
    field_type: field.type,
    is_internal: Boolean(field.is_internal),
  }));
}

async function fetchLegacy(
  tenantId: string | undefined,
  deskSegment: string,
): Promise<LegacyCustomFieldDefinition[]> {
  const { data } = await backofficeClient.get<LegacyDefinitionsListResponse>(
    `${LEGACY_BASE}/${encodeURIComponent(deskSegment)}/custom-fields/`,
    { params: { tenant_id: tenantId } },
  );
  return data.items;
}

/** Definitions deduplicated by id and sorted by name. */
export function mergeDefinitions<
  T extends { id: string | number; name: string },
>(lists: (T[] | undefined)[]): T[] {
  const byId = new Map<string, T>();
  for (const def of lists.flatMap((l) => l ?? [])) {
    if (!byId.has(String(def.id))) byId.set(String(def.id), def);
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Custom fields a ticket on any of `deskIds` can carry: the tenant-wide ones
 * plus each desk's own, keyed by their config field id.
 *
 * `legacyDefinitions` are the pre-config definitions, only for naming
 * mappings saved under their integer id; new mappings use `definitions`.
 */
export function useCustomFieldDefinitions(
  tenantId: string | undefined,
  deskIds: string[],
) {
  const scopes: (string | null)[] = [null, ...deskIds];
  const configs = useQueries({
    queries: scopes.map((deskId) => ({
      queryKey: ["service-desk", "custom-field-config", tenantId, deskId],
      queryFn: () => fetchConfig(tenantId, deskId),
      enabled: Boolean(tenantId),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const legacyGlobal = useQuery({
    queryKey: ["service-desk", "custom-fields", tenantId, "all"],
    queryFn: () => fetchLegacy(tenantId, "all"),
    enabled: Boolean(tenantId),
    staleTime: 5 * 60 * 1000,
  });
  const legacyPerDesk = useQueries({
    queries: deskIds.map((deskId) => ({
      queryKey: ["service-desk", "custom-fields", tenantId, deskId],
      queryFn: () => fetchLegacy(tenantId, deskId),
      enabled: Boolean(tenantId),
      staleTime: 5 * 60 * 1000,
    })),
  });

  return {
    definitions: mergeDefinitions(configs.map((q) => q.data)),
    legacyDefinitions: mergeDefinitions([
      legacyGlobal.data,
      ...legacyPerDesk.map((q) => q.data),
    ]),
    isLoading:
      configs.some((q) => q.isLoading) ||
      legacyGlobal.isLoading ||
      legacyPerDesk.some((q) => q.isLoading),
  };
}
